import React, { useEffect, useState } from 'react';
import { Form, FormInstance } from 'antd';
import {
  DatePicker,
  Input,
  Modal,
  Select,
  Spinner,
} from 'UI';
import { Organization, Variety } from 'constants/interface';
import { useDispatch, useSelector } from 'hooks/store';
import {
  cleanVarietiesAction,
  getVarietyListAction,
  isLoadingVarietiesSelector,
  varietiesEntitiesSelector,
  varietiesIdsSelector,
} from 'state/slices/products';
import dayjs, { Dayjs } from 'dayjs';
import { updateEntryPrices } from 'helpers/inventory';
import { PlusOutlined } from '@ant-design/icons';

import {
  FormContainer,
  BaseFormItem,
  InlineButton,
  RemoteSelectContent,
  RemoteSelectSpinnerContainer,
  NotesInline,
  AddEntryItemButton,
  EntryItemFormRow,
  EntryItemInput,
  RemoveEntryItemButton,
  RemoveEntryItemIcon,
  FormItemRow,
} from './style';

interface EntryItemFormData {
  variety: string
  quantity: string
  unitPrice: string
  totalPrice: string
  batch: string
  expiryDate: Dayjs
  germinationRate: string
}

export interface EntryInputFormData {
  entryDate: Dayjs
  provider: string
  receivingTermCode: string
  deliveredBy: string
  receivedBy: string
  fiscalNote: string
  notes: string
  items: EntryItemFormData[]
}

interface EntryInputModalProps {
  formHandler: FormInstance
  onSubmit(data: unknown): void
  onCancel(): void
  isLoading: boolean
  isVisible: boolean
  activeOrganization: Organization
}

const EntryInputModal = ({
  formHandler,
  onSubmit,
  onCancel,
  isLoading,
  isVisible,
  activeOrganization,
}: EntryInputModalProps): JSX.Element => {
  const dispatch = useDispatch();

  const varietySearchEntities = useSelector(varietiesEntitiesSelector);
  const varietySearchIds = useSelector(varietiesIdsSelector);
  const isLoadingVarieties = useSelector(isLoadingVarietiesSelector);
  const varietySearchResult = varietySearchIds
    .map((varietyId) => varietySearchEntities[varietyId])
    .filter((variety) => !!variety) as Variety[];
  const [searchIndex, setSearchIndex] = useState<number>();

  const [internalLoading, setInternalLoading] = useState(false);

  const [
    varietySearchTimeout,
    setVarietySearchTimeout,
  ] = useState<number>();
  const onVarietySearch = (search: string, index: number) => {
    if (!search) {
      dispatch(cleanVarietiesAction());
      setSearchIndex(undefined);
    } else {
      setInternalLoading(true);
      setSearchIndex(index);
      window.clearTimeout(varietySearchTimeout);
      const searchTimeout = window.setTimeout(() => {
        dispatch(getVarietyListAction({
          search,
          isPaginated: false,
        }));
        setInternalLoading(false);
      }, 500);
      setVarietySearchTimeout(searchTimeout);
    }
  };

  useEffect(() => {
    const clearEffects = () => {
      window.clearTimeout(varietySearchTimeout);
    };
    return clearEffects;
  }, []);

  const [selectedVarieties, setSelectedVarieties] = useState<(Variety | undefined)[]>([]);

  const internalOnCancel = () => {
    setSelectedVarieties([]);
    dispatch(cleanVarietiesAction());
    onCancel();
  };

  const onEntryItemChange = (index: number, field?: 'unit' | 'total') => {
    const currentFormItems: EntryItemFormData[] = formHandler.getFieldValue('items');
    if (!currentFormItems) {
      return;
    }

    const entryItem = currentFormItems[index];

    if (!entryItem) {
      return;
    }

    const updatedPrices = updateEntryPrices(entryItem, field);

    const updatedFormItems: typeof currentFormItems = [
      ...currentFormItems.slice(0, index),
      {
        ...entryItem,
        ...updatedPrices,
        quantity: entryItem.quantity,
      },
      ...currentFormItems.slice(index + 1),
    ];

    formHandler.setFields([{
      name: 'items',
      value: updatedFormItems,
    }]);
  };

  const onEntryItemAdded = (
    formAdd: (defaultValue?: any, index?: number) => void, // eslint-disable-line
    index: number,
  ) => {
    const updatedSelectedVarieties = [
      ...selectedVarieties.slice(0, index),
      undefined,
      ...selectedVarieties.slice(index + 1),
    ];
    setSelectedVarieties(updatedSelectedVarieties);
    formAdd({
      varietyId: undefined,
    }, index);
  };

  const onEntryItemRemoved = (formRemove: (index: number | number[]) => void, index: number) => {
    const updatedSelectedVarieties = [
      ...selectedVarieties.slice(0, index),
      ...selectedVarieties.slice(index + 1),
    ];
    setSelectedVarieties(updatedSelectedVarieties);
    formRemove(index);
  };

  const onVarietySelect = (variety: Variety | undefined, index: number) => {
    if (variety) {
      const updatedSelectedVarieties = [
        ...selectedVarieties.slice(0, index),
        { ...variety },
        ...selectedVarieties.slice(index + 1),
      ];
      setSelectedVarieties(updatedSelectedVarieties);
      dispatch(cleanVarietiesAction());
      setSearchIndex(undefined);
    }
  };

  return (
    <Modal
      visible={isVisible}
      title="Nova entrada"
      destroyOnClose
      onCancel={internalOnCancel}
      afterClose={internalOnCancel}
      zIndex={90}
      width={1200}
      footer={[
        <InlineButton
          key="cancel"
          onClick={internalOnCancel}
          type="ghost"
        >
          Cancelar
        </InlineButton>,
        <InlineButton
          key="submit"
          onClick={onSubmit}
          disabled={isLoading}
          loading={isLoading}
        >
          Salvar
        </InlineButton>,
      ]}
    >
      <FormContainer
        layout="vertical"
        form={formHandler}
        requiredMark={false}
        onFinish={onSubmit as (val: unknown) => void}
      >
        <BaseFormItem
          label="Unidade"
          name="organization"
          initialValue={activeOrganization.name}
        >
          <Input disabled />
        </BaseFormItem>
        <BaseFormItem
          label="Data de entrada"
          name="entryDate"
          initialValue={dayjs()}
          rules={[
            {
              required: true,
              message: 'Insira uma data de entrada',
            },
          ]}
        >
          <DatePicker disabled={isLoading} />
        </BaseFormItem>
        <BaseFormItem
          label="Nota fiscal"
          name="fiscalNote"
        >
          <Input disabled={isLoading} />
        </BaseFormItem>
        <BaseFormItem
          label="Fornecedor"
          name="provider"
          rules={[
            {
              required: true,
              message: 'Insira um fornecedor',
            },
          ]}
        >
          <Input disabled={isLoading} />
        </BaseFormItem>
        <h3>Itens</h3>
        <Form.List
          name="items"
          rules={[{
            message: 'Insira pelo menos um item',
            validator: () => {
              const formItems: EntryItemFormData[] = formHandler.getFieldValue('items');
              if (formItems.length > 0) {
                return Promise.resolve();
              }

              return Promise.reject();
            },
          }]}
        >
          {(fields, { add, remove }) => (
            <>
              {fields.map((itemField, index) => (
                <EntryItemFormRow
                  key={itemField.key}
                >
                  <EntryItemInput
                    label="Item"
                    style={{
                      flex: '2',
                    }}
                    name={[itemField.name, 'variety']}
                    validateTrigger="onSubmit"
                    rules={[
                      {
                        required: true,
                        message: 'Selecione um item',
                      },
                    ]}
                  >
                    <Select
                      placeholder="Selecione um item"
                      disabled={isLoading}
                      showArrow={false}
                      showSearch
                      onSearch={(searchValue) => onVarietySearch(searchValue, index)}
                      filterOption={false}
                      onSelect={(
                        varietyId,
                      ) => onVarietySelect(varietySearchEntities[varietyId], index)}
                      notFoundContent={<></>}
                      dropdownRender={(menu) => (
                        <div>
                          <RemoteSelectContent>
                            {menu}
                            {(internalLoading || isLoadingVarieties) && (
                              <RemoteSelectSpinnerContainer>
                                <Spinner />
                              </RemoteSelectSpinnerContainer>
                            )}
                          </RemoteSelectContent>
                        </div>
                      )}
                    >
                      {searchIndex === index
                        ? varietySearchResult.map((varietyOption) => (
                          <Select.Option
                            value={varietyOption.id}
                            key={varietyOption.id}
                          >
                            {`${varietyOption.culture.name} ${varietyOption.name}`}
                          </Select.Option>
                        ))
                        : (() => {
                          const varietyOption = selectedVarieties[index];
                          return varietyOption ? (
                            <Select.Option
                              value={varietyOption.id}
                              key={varietyOption.id}
                            >
                              {`${varietyOption.culture.name} ${varietyOption.name}`}
                            </Select.Option>
                          ) : null;
                        })()}
                    </Select>
                  </EntryItemInput>
                  <EntryItemInput
                    label="Lote"
                    name={[itemField.name, 'batch']}
                    rules={[
                      {
                        required: true,
                        message: 'Insira um lote',
                      },
                    ]}
                  >
                    <Input disabled={isLoading || !selectedVarieties[index]} />
                  </EntryItemInput>
                  <EntryItemInput
                    label="Pct. de germinação"
                    name={[itemField.name, 'germinationRate']}
                    rules={[
                      {
                        required: true,
                        message: 'Preencha uma porcentagem de germinação',
                      },
                    ]}
                    // initialValue={100}
                  >
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      disabled={isLoading || !selectedVarieties[index]}
                      suffix="%"
                    />
                  </EntryItemInput>
                  <EntryItemInput
                    label="Validade"
                    name={[itemField.name, 'expiryDate']}
                    rules={[
                      {
                        required: true,
                        message: 'Insira uma validade',
                      },
                    ]}
                  >
                    <DatePicker
                      disabled={isLoading || !selectedVarieties[index]}
                    />
                  </EntryItemInput>
                  <EntryItemInput
                    label="Quantidade"
                    name={[itemField.name, 'quantity']}
                    rules={[
                      {
                        required: true,
                        message: 'Insira uma quantidade',
                      },
                    ]}
                  >
                    <Input
                      onChange={() => onEntryItemChange(index)}
                      type="number"
                      disabled={isLoading || !selectedVarieties[index]}
                      suffix={selectedVarieties[index] ? 'Sem.' : ''}
                    />
                  </EntryItemInput>
                  <EntryItemInput
                    label="Preço Unitário"
                    name={[itemField.name, 'unitPrice']}
                    rules={[
                      {
                        required: true,
                        message: 'Insira um preço',
                      },
                    ]}
                  >
                    <Input
                      onChange={() => onEntryItemChange(index, 'unit')}
                      type="number"
                      disabled={isLoading || !selectedVarieties[index]}
                      prefix="R$"
                    />
                  </EntryItemInput>
                  <EntryItemInput
                    label="Valor"
                    name={[itemField.name, 'totalPrice']}
                    rules={[
                      {
                        required: true,
                        message: 'Insira um preço',
                      },
                    ]}
                  >
                    <Input
                      onChange={() => onEntryItemChange(index, 'total')}
                      type="number"
                      disabled={isLoading || !selectedVarieties[index]}
                      prefix="R$"
                    />
                  </EntryItemInput>
                  <RemoveEntryItemButton onClick={() => onEntryItemRemoved(remove, index)}>
                    <RemoveEntryItemIcon />
                  </RemoveEntryItemButton>
                </EntryItemFormRow>
              ))}
              <AddEntryItemButton
                type="dashed"
                onClick={() => onEntryItemAdded(add, fields.length)}
                icon={<PlusOutlined size={14} />}
              >
                Adicionar item
              </AddEntryItemButton>
            </>
          )}
        </Form.List>
        <BaseFormItem
          label="Termo de Conformidade"
          name="receivingTermCode"
          rules={[
            {
              required: true,
              message: 'Insira o termo de conformidade',
            },
          ]}
        >
          <Input disabled={isLoading} />
        </BaseFormItem>
        <FormItemRow>
          <BaseFormItem
            label="Entregue por"
            name="deliveredBy"
            rules={[
              {
                required: true,
                message: 'Insira um nome',
              },
            ]}
          >
            <Input disabled={isLoading} />
          </BaseFormItem>
          <BaseFormItem
            label="Recebido por"
            name="receivedBy"
            rules={[
              {
                required: true,
                message: 'Insira um nome',
              },
            ]}
          >
            <Input disabled={isLoading} />
          </BaseFormItem>
        </FormItemRow>
        <NotesInline
          label="Observações"
          name="notes"
        >
          <Input.TextArea disabled={isLoading} rows={5} />
        </NotesInline>
      </FormContainer>
    </Modal>
  );
};

export default EntryInputModal;
